// XLSX export for the Outreach list: one flat sheet, one row per candidate,
// with each logged attempt contributing its own group of columns.
import { fmtDate } from "./theme";
import { calendarDaysSince } from "./outreachDates";
import { ensureXLSX } from "./xlsx";

// Attempts are capped at 3 by the DB, but the column count is derived from
// the data so an extra attempt could never be silently dropped.
const MIN_ATTEMPT_COLUMNS = 3;

// Blank rather than "—" so empty cells stay empty in a spreadsheet.
const val = (v) => (v == null ? "" : v);

// Matches the list/detail display rule: once removed, a row reads as
// Archived regardless of whatever priority value is still on it.
const priorityLabel = (r) =>
  r.removed_at != null || r.priority == null ? "Archived" : `Tier ${r.priority}`;

// A launch is recorded by the archiving cron as removal with the reserved
// 'launched' reason, not by the date_launched column.
const launchedFromArchive = (r) =>
  r.removed_at != null && (r.reason_for_archive || "").toLowerCase() === "launched";

// Keyed by attempt_number rather than array position so a gap in the
// numbering could never shift an attempt into the wrong column group.
const attemptsByNumber = (r) =>
  new Map((r.attempts || []).map((a) => [Number(a.attempt_number), a]));

const highestAttemptNumber = (r) => {
  const numbers = [...attemptsByNumber(r).keys()];
  return numbers.length > 0 ? Math.max(...numbers) : 0;
};

// json_to_sheet derives the header row from the first object's keys, so every
// row has to carry the identical key set in the same order — candidates with
// fewer attempts get empty strings rather than missing keys.
export const buildExportRows = (rows) => {
  const attemptColumns = Math.max(MIN_ATTEMPT_COLUMNS, ...rows.map(highestAttemptNumber));

  return rows.map((r) => {
    const out = {
      "Priority": priorityLabel(r),
      "Provider Name": val(r.provider_name),
      "Email": val(r.email),
      "Station": val(r.station_id),
      "Station Name": val(r.station_name),
      "VISN": val(r.visn),
      "Specialty": val(r.specialty),
      "Days Idle": val(r.days_idle),
      "Days Since Last Attempt": val(calendarDaysSince(r.last_attempt_at)),
      // Last recorded audio note, not last outreach attempt — see the
      // tooltip on this column in Outreach.js for the same distinction.
      "Last Appointment Date": r.last_note_date ? fmtDate(r.last_note_date) : "",
    };

    const attempts = attemptsByNumber(r);
    const lastNumber = highestAttemptNumber(r);
    // Only the attempt the launch followed is credited with it, so the
    // sheet shows which attempt actually worked.
    const launchedAfter = launchedFromArchive(r) ? lastNumber : 0;

    for (let n = 1; n <= attemptColumns; n++) {
      const attempt = attempts.get(n);
      out[`Attempt ${n} Date`] = attempt ? fmtDate(attempt.attempt_created_at) : "";
      out[`Attempt ${n} Type`] = attempt ? val(attempt.attempt_type) : "";
      out[`Attempt ${n} Response`] = attempt ? val(attempt.provider_feedback) : "";
      out[`Attempt ${n} By`] = attempt ? val(attempt.attempt_by) : "";
      out[`Launched After Attempt ${n}?`] = attempt ? (n === launchedAfter ? "Yes" : "No") : "";
    }

    // Just the latest attempt's note — one plain value, so it stays
    // readable next to the per-attempt columns.
    const lastAttempt = attempts.get(lastNumber);
    out["Notes"] = lastAttempt ? val(lastAttempt.attempt_note) : "";

    return out;
  });
};

export const downloadOutreachXlsx = async (rows) => {
  const XLSX = await ensureXLSX();
  const ws = XLSX.utils.json_to_sheet(buildExportRows(rows));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Outreach Candidates");
  XLSX.writeFile(wb, `outreach_candidates_${new Date().toISOString().slice(0, 10)}.xlsx`);
};
